import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Rocket, Users, Loader2, RefreshCw, Sparkles, Plus, X, Search, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { getCategoryPreset } from "../../lib/categoryConfig";
import AdminDesignTokensPanel from "./AdminDesignTokensPanel";
import InfoTooltip from "./InfoTooltip";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STATUS_LABELS = { live: "Live", waitlist: "Waitlist (pilot)", hidden: "Hidden" };

export default function LaunchControlPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixSaving, setMatrixSaving] = useState(null);

  const getAuth = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/launch-control`, { headers: getAuth() });
      if (res.ok) setData(await res.json());
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, [getAuth]);
  
  const fetchMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const res = await fetch(`${API}/admin/launch-control/matrix`, { headers: getAuth() });
      if (res.ok) {
        const d = await res.json();
        setMatrix(d.cells || []);
      }
    } catch { /* silent */ }
    setMatrixLoading(false);
  }, [getAuth]);

  useEffect(() => { fetchData(); fetchMatrix(); }, [fetchData, fetchMatrix]);

  const updateMatrixCell = async (categoryId, city, status) => {
    const cellKey = `${categoryId}-${city}`;
    setMatrixSaving(cellKey);
    try {
      const res = await fetch(`${API}/admin/category-launch-control/${categoryId}/${city}`, {
        method: "PATCH",
        headers: { ...getAuth(), "Content-Type": "application/json" },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        toast.success(`${categoryId} in ${city}: ${status}`);
        fetchMatrix();
      } else {
        toast.error("Failed to update");
      }
    } catch { toast.error("Failed"); }
    setMatrixSaving(null);
  };

  const updateStatus = async (slug, newStatus) => {
    setUpdating(slug);
    try {
      const res = await fetch(`${API}/admin/launch-control/${slug}/status`, {
        method: "PUT", headers: { ...getAuth(), "Content-Type": "application/json" },
        body: JSON.stringify({ launch_status: newStatus })
      });
      if (res.ok) { toast.success(`${slug} set to ${newStatus}`); fetchData(); }

      // Iter 9.40 Pass D - bidirectional cascade: when top-level status flips,
      // mirror to every city cell for this category. "live" → "launched",
      // anything else → "waitlist". Run in parallel (best-effort, non-fatal).
      const cellStatus = newStatus === "live" ? "launched" : "waitlist";
      const cells = matrix.filter((c) => c.category_id === slug);
      if (cells.length) {
        await Promise.all(cells.map((c) =>
          fetch(`${API}/admin/category-launch-control/${slug}/${c.city}`, {
            method: "PATCH",
            headers: { ...getAuth(), "Content-Type": "application/json" },
            body: JSON.stringify({ status: cellStatus }),
          }).catch(() => {})
        ));
        fetchMatrix();
      }
    } catch { toast.error("Failed"); }
    setUpdating(null);
  };

  const updateTierVisibility = async (tier, visibility) => {
    try {
      await fetch(`${API}/admin/tier-visibility`, {
        method: "PUT", headers: { ...getAuth(), "Content-Type": "application/json" },
        body: JSON.stringify({ [tier]: visibility })
      });
      toast.success(`${tier} tier ${visibility}`);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

  const cats = data?.categories || [];
  const waitlistSignups = cats.reduce((s, c) => s + (c.waitlist_count || 0), 0);
  // Iter 9.40 Pass H - city-cell stats come from db.launch_control rows with a city set
  const cellsFromMatrix = matrix || [];
  const liveCategoryCount = cats.filter(c => (c.launch_status === "live")).length;
  const waitlistCategoryCount = cats.filter(c => (c.launch_status || "waitlist") === "waitlist").length;
  const hiddenCategoryCount = cats.filter(c => c.launch_status === "hidden").length;
  const liveCityCells = cellsFromMatrix.filter(c => c.status === "launched" || c.status === "live").length;
  const waitlistCityCells = cellsFromMatrix.filter(c => (c.status || "waitlist") === "waitlist").length;

  return (
    <div className="space-y-5" data-testid="launch-control-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Rocket className="w-5 h-5" /> Launch Control
            <InfoTooltip
              label="Launch Control"
              description="Per-category, per-city launch matrix. Status flips between waitlist, pilot, and live. Public Become a Provider gates and category visibility on /me Plan a Service all read from this matrix."
              source="db.category_launch_control"
              where_synced={["GET /api/category-launch-status", "Public /join?category= waitlist gate", "Provider onboarding eligibility check"]}
            />
          </h2>
          <p className="text-sm text-gray-500">Manage category launches and subscription tier visibility</p>
        </div>
        <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
      </div>

      {/* Summary stats - Iter 9.40 Pass H: kill Pilot, split by category vs city activation, add waitlist signups */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="launch-stats">
        {[
          { label: "Live categories",      value: liveCategoryCount,     color: "text-green-700 bg-green-50",   testid: "stat-live-cats" },
          { label: "Waitlist categories",  value: waitlistCategoryCount, color: "text-amber-700 bg-amber-50",   testid: "stat-waitlist-cats" },
          { label: "Live city activations",     value: liveCityCells,         color: "text-green-700 bg-green-50",   testid: "stat-live-cells" },
          { label: "Waitlist city activations", value: waitlistCityCells,     color: "text-amber-700 bg-amber-50",   testid: "stat-waitlist-cells" },
          { label: "Waitlist signups",     value: waitlistSignups,       color: "text-blue-700 bg-blue-50",     testid: "stat-waitlist-signups" },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`} data-testid={s.testid}>
            <div className="text-2xl font-bold">{s.value}</div>
            <div className="text-[11px] font-medium leading-tight">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Category grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {cats.map(cat => {
          const preset = getCategoryPreset(cat.slug);
          const Icon = preset?.icon || Sparkles;
          // Iter 9.40 Pass F - normalize any non-`live`/`hidden` status into
          // `waitlist` so the dropdown shows the correct value (was silently
          // falling back to "Live" when backend returned `coming_soon`/`pilot`).
          const rawStatus = cat.launch_status || "waitlist";
          const status = (rawStatus === "live" || rawStatus === "hidden")
            ? rawStatus
            : "waitlist";
          return (
            <Card key={cat.slug} className="overflow-hidden" data-testid={`launch-cat-${cat.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: preset?.bgColor || "#f3f4f6" }}>
                      <Icon className="w-4 h-4" style={{ color: preset?.iconColor || "#6b7280" }} />
                    </div>
                    <span className="font-medium text-sm capitalize">{cat.name}</span>
                  </div>
                  <select
                    value={status}
                    onChange={e => updateStatus(cat.slug, e.target.value)}
                    disabled={updating === cat.slug}
                    className={`text-xs font-medium px-2 py-1 rounded-lg border cursor-pointer ${
                      status === "live" ? "bg-green-50 border-green-200 text-green-700" :
                      status === "waitlist" ? "bg-amber-50 border-amber-200 text-amber-700" :
                      "bg-gray-100 border-gray-200 text-gray-500"
                    }`}
                    data-testid={`launch-status-${cat.slug}`}
                  >
                    <option value="waitlist">Waitlist</option>
                    <option value="live">Live</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {cat.pilot_count} hosts</span>
                  <span>{cat.event_count} dashboards</span>
                  {cat.waitlist_count > 0 && <span className="text-blue-600 font-medium">{cat.waitlist_count} waitlisted</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Category x City matrix */}
      <div className="mt-6 border-t pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2"><Users className="w-4 h-4" /> Category × City Matrix</h3>
            <p className="text-xs text-gray-500">Per-city launch state. Switch a cell from Waitlist to Live to open public signups.</p>
          </div>
          <div className="flex items-center gap-2">
            <CityAddPicker onAdd={async (city) => {
              try {
                await fetch(`${API}/admin/launch-control/matrix/cities`, {
                  method: "POST",
                  headers: { ...getAuth(), "Content-Type": "application/json" },
                  body: JSON.stringify({ city }),
                });
                toast.success(`${city} added`);
                fetchMatrix();
              } catch { toast.error("Add failed"); }
            }} />
            <button onClick={fetchMatrix} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="matrix-refresh">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
        {matrixLoading ? (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : matrix.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3">No matrix cells found</p>
        ) : (
          (() => {
            // Iter 9.37 - preserve admin-defined order from platform_settings.city_matrix_order
            const orderFromAdmin = data?.city_matrix_order || [];
            const allCities = Array.from(new Set(matrix.map(c => c.city)));
            const cities = [
              ...orderFromAdmin.filter(c => allCities.includes(c)),
              ...allCities.filter(c => !orderFromAdmin.includes(c)).sort(),
            ];
            const cats = Array.from(new Set(matrix.map(c => c.category_id))).sort();
            const cellMap = {};
            matrix.forEach(c => { cellMap[`${c.category_id}-${c.city}`] = c; });
            const moveCity = async (city, dir) => {
              const idx = cities.indexOf(city);
              const newIdx = idx + dir;
              if (newIdx < 0 || newIdx >= cities.length) return;
              const next = [...cities];
              [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
              try {
                await fetch(`${API}/admin/launch-control/matrix/order`, {
                  method: "PUT",
                  headers: { ...getAuth(), "Content-Type": "application/json" },
                  body: JSON.stringify({ city_matrix_order: next }),
                });
                fetchData();
              } catch { toast.error("Reorder failed"); }
            };
            const removeCity = async (city) => {
              if (!window.confirm(`Remove ${city} from the matrix? All cells for this city are deleted.`)) return;
              try {
                await fetch(`${API}/admin/launch-control/matrix/cities/${encodeURIComponent(city)}`, {
                  method: "DELETE", headers: getAuth(),
                });
                toast.success(`${city} removed`);
                fetchMatrix();
              } catch { toast.error("Remove failed"); }
            };
            return (
              <div className="overflow-x-auto border rounded-lg" data-testid="launch-matrix-table">
                <table className="text-xs w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600 sticky left-0 bg-gray-50">Category</th>
                      {cities.map((c, idx) => (
                        <th key={c} className="px-2 py-2 font-medium text-gray-600 text-center capitalize">
                          <div className="flex items-center justify-center gap-0.5">
                            <button onClick={() => moveCity(c, -1)} disabled={idx === 0} className="text-gray-300 hover:text-gray-600 disabled:opacity-20" data-testid={`city-up-${c}`} title="Move left">
                              <ArrowUp className="w-3 h-3 rotate-[-90deg]" />
                            </button>
                            <span data-testid={`matrix-city-header-${c}`}>{c}</span>
                            <button onClick={() => moveCity(c, 1)} disabled={idx === cities.length - 1} className="text-gray-300 hover:text-gray-600 disabled:opacity-20" data-testid={`city-down-${c}`} title="Move right">
                              <ArrowDown className="w-3 h-3 rotate-[-90deg]" />
                            </button>
                            <button onClick={() => removeCity(c)} className="ml-1 text-gray-300 hover:text-red-500" data-testid={`city-remove-${c}`} title="Remove city">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cats.map(catId => (
                      <tr key={catId} className="border-t">
                        <td className="px-3 py-2 font-medium text-gray-700 capitalize sticky left-0 bg-white">{catId.replace(/^cat_/, "")}</td>
                        {cities.map(city => {
                          const cell = cellMap[`${catId}-${city}`];
                          if (!cell) return <td key={city} className="px-2 py-2 text-center text-gray-300">-</td>;
                          const cellKey = `${catId}-${city}`;
                          const isSaving = matrixSaving === cellKey;
                          return (
                            <td key={city} className="px-1.5 py-1.5 text-center">
                              <select
                                value={cell.status}
                                onChange={(e) => updateMatrixCell(catId, city, e.target.value)}
                                disabled={isSaving}
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded border cursor-pointer w-full ${
                                  cell.status === "launched" ? "bg-green-50 border-green-200 text-green-700" :
                                  "bg-amber-50 border-amber-200 text-amber-700"
                                }`}
                                data-testid={`matrix-cell-${catId}-${city}`}
                              >
                                <option value="waitlist">Waitlist</option>
                                <option value="launched">Live</option>
                              </select>
                              {(cell.waitlist_signups > 0 || cell.active_providers > 0) && (
                                <div className="text-[9px] text-gray-400 mt-0.5">
                                  {cell.waitlist_signups}w · {cell.active_providers}p
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()
        )}
      </div>

      {/* Admin Design Tokens (global UI tokens) */}
      <div className="mt-6 border-t pt-5">
        <AdminDesignTokensPanel />
      </div>

    </div>
  );
}

// Iter 9.37 - search-and-add city picker using Google Places autocomplete
function CityAddPicker({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/places/autocomplete?input=${encodeURIComponent(query)}&types=(cities)`);
        const d = await r.json();
        setResults((d.predictions || []).slice(0, 6));
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (p) => {
    const city = p.structured_formatting?.main_text || p.description?.split(",")[0];
    if (city) onAdd(city.trim());
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg" data-testid="city-add-open">
        <Plus className="w-3 h-3" /> Add city
      </button>
    );
  }
  return (
    <div className="relative">
      <div className="flex items-center gap-1 bg-white border rounded-lg px-2 py-1.5">
        <Search className="w-3 h-3 text-gray-400" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search any city worldwide…"
          className="text-xs outline-none w-44"
          data-testid="city-add-input"
        />
        <button onClick={() => { setOpen(false); setQuery(""); }} className="text-gray-400 hover:text-gray-600" data-testid="city-add-close">
          <X className="w-3 h-3" />
        </button>
      </div>
      {(loading || results.length > 0) && (
        <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[260px]" data-testid="city-add-results">
          {loading && <div className="p-2 text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
          {!loading && results.map((p) => (
            <button
              key={p.place_id || p.description}
              onClick={() => pick(p)}
              className="block w-full text-left px-3 py-2 text-xs hover:bg-purple-50"
              data-testid={`city-add-result-${(p.structured_formatting?.main_text || p.description || "").toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="font-medium text-gray-800">{p.structured_formatting?.main_text || p.description}</div>
              {p.structured_formatting?.secondary_text && (
                <div className="text-[10px] text-gray-400">{p.structured_formatting.secondary_text}</div>
              )}
            </button>
          ))}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="p-2 text-xs text-gray-400">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}
